/** tm/components/GovFeedTable.tsx — 공고 검토함 대량 검토 표 · 칸 인라인 편집 (2026-09-30)
 *
 *  · 표: 고정 머리(sticky) · 두 줄 행(사업명 + 기관·원공고명) · 폭이 좁으면 낮은 우선순위 열(출처 → 대상)부터 숨긴다.
 *    패널이 열리면 compact(☐ · 적합도 · 사업명 · 확인) 로 줄어 오른쪽 패널에 자리를 준다.
 *  · 칸 편집: 사업명 · 지역 · 분류 · 지원금액 — 등록 양식(GovProgramForm)과 같은 Pill · GovCategoryPicker 를 팝오버로.
 *    저장 값은 gov_programs payload 조각(PUT draft) — 부모가 서버 저장 · 행 갱신을 맡는다.
 */
import { useMemo, useRef, useState } from 'react';
import { C, T, IMEText, REGION_CITIES } from './ui';
import { Pill, REGIONS } from './GovProgramForm';
import { GOV_SPECIAL } from './GovTaxonomy';
import { GovCategoryPicker } from './GovTaxonomy';
import {
  AMBER, SOURCE_LABEL, programOf, displayNameOf, isPastProg, targetOf, shortDate, isProjected, fmtManwon, parseManwon,
  FeedPopover, PopoverFoot, type FeedProg,
} from './GovFeedParts';
import type { GovFeedRow, GovProgram, GovFeedSort, GovFeedSortKey } from '../types';

export type FeedEditField = 'name' | 'region' | 'cat' | 'amount';
type ColKey = 'chk' | 'star' | 'drop' | 'fit' | 'name' | 'region' | 'cat' | 'amount' | 'target' | 'sched' | 'unsure' | 'src';

const COLS: { k: ColKey; label: string; w: number; align?: 'right' | 'center' }[] = [
  { k: 'chk', label: '', w: 36 },
  { k: 'star', label: '', w: 28, align: 'center' },
  { k: 'drop', label: '', w: 28, align: 'center' },   // 버리기 (분석 대기 탭 · 2026-10-04)
  { k: 'fit', label: '적합도', w: 52, align: 'center' },
  { k: 'name', label: '사업명', w: 0 },
  { k: 'region', label: '지역', w: 88 },
  { k: 'cat', label: '분류', w: 112 },
  { k: 'amount', label: '지원금액', w: 80, align: 'right' },
  { k: 'target', label: '대상', w: 128 },
  { k: 'sched', label: '일정', w: 108 },
  { k: 'unsure', label: '확인', w: 44, align: 'center' },
  { k: 'src', label: '출처', w: 76 },
];
const NAME_MIN = 224;
/** 머리글 정렬 (2026-10-03) — 열 → 서버 정렬 키 · 같은 열을 누르면 오름 → 내림 → 원래 순서 */
const SORT_OF: Partial<Record<ColKey, GovFeedSortKey>> = { fit: 'fit', name: 'name', region: 'region', cat: 'cat', amount: 'amount', sched: 'deadline', src: 'collected' };   // 출처 = 불러온 날(새로 들어온 것 먼저 · 2026-10-14 지도사 「14일에 수집했는데 왜 하나도 없어」)
const SORT_TIP: Record<GovFeedSortKey, string> = { fit: '적합도', name: '사업명 가나다', region: '지역 (전국 → 서울·경기·인천 → 그 밖)', cat: '분류 (지도 순서)', amount: '기업당 최대 금액', deadline: '마감일 (선착순·상시는 뒤)', collected: '불러온 날 (새로 들어온 것 먼저)' };
const DROP_ORDER: ColKey[] = ['src', 'target', 'cat', 'region'];

/** 폭에 맞는 열 — compact 면 핵심 4열 */
export function visibleCols(width: number, compact: boolean, slim = false): ColKey[] {
  if (slim) return ['fit'];
  if (compact) return ['chk', 'star', 'drop', 'fit', 'name', 'unsure'];
  let keys = COLS.map(c => c.k);
  const need = () => COLS.filter(c => keys.includes(c.k)).reduce((s, c) => s + c.w, 0) + NAME_MIN;
  for (const d of DROP_ORDER) { if (need() <= width) break; keys = keys.filter(k => k !== d); }
  return keys;
}

const STYLE = `
.gf-sort{display:inline-flex;align-items:center;gap:3px;width:100%;border:none;background:transparent;padding:0;margin:0;font:inherit;color:inherit;cursor:pointer;white-space:nowrap}
.gf-sort .gf-sort-ic{font-size:9px;color:${C.borderDk};opacity:0;transition:opacity .12s}
.gf-sort:hover{color:${C.text}}
.gf-sort:hover .gf-sort-ic{opacity:1}
.gf-sort.on{color:${C.accent}}
.gf-sort.on .gf-sort-ic{opacity:1;color:${C.accent}}
.gf-sort:focus-visible{outline:2px solid ${C.accentLt};outline-offset:2px;border-radius:3px}
.gf-tb{width:100%;border-collapse:separate;border-spacing:0;table-layout:fixed}
.gf-tb th{position:sticky;top:0;z-index:2;background:${C.bgAlt};border-bottom:1px solid ${C.border};font-size:${T.xs}px;font-weight:600;color:${C.sub};text-align:left;padding:0 8px;height:34px;white-space:nowrap}
.gf-tb td{border-bottom:1px solid ${C.border};padding:0 8px;height:52px;font-size:${T.sm}px;color:${C.text};vertical-align:middle;overflow:hidden}
.gf-tb tr.gf-r{cursor:pointer}
.gf-tb tr.gf-r:hover td{background:#F9FAFB}
.gf-tb tr.gf-r.gf-chk td{background:#F5F9FF}
.gf-tb tr.gf-r.gf-cur td{background:#F3F6FB}
.gf-tb tr.gf-r.gf-sel td{background:${C.accentLt}}
.gf-ed{display:block;margin:0 -6px;padding:5px 6px;border-radius:6px;border:1px solid transparent;min-height:20px}
.gf-tb tr.gf-r:hover .gf-ed{border-color:${C.border};background:${C.white}}
.gf-ed:hover{border-color:${C.accent}!important;box-shadow:0 0 0 2px ${C.accentLt}}
.gf-1{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.gf-2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;word-break:keep-all;line-height:1.35}
.gf-tb td.gf-cb{user-select:none}
.gf-num{font-variant-numeric:tabular-nums}
.gf-star{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;margin:0 -4px;border:none;border-radius:6px;background:transparent;cursor:pointer;padding:0;color:${C.borderDk};transition:color .12s,background .12s,transform .12s}
.gf-star:hover{background:#FEF3C7;color:#D97706}
.gf-star.on{color:#F59E0B}
.gf-star.on:hover{color:#B45309}
.gf-star:active{transform:scale(.85)}
.gf-star:focus-visible{outline:2px solid ${C.accentLt};outline-offset:1px}
.gf-drop{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;margin:0 -4px;border:none;border-radius:6px;background:transparent;cursor:pointer;padding:0;color:${C.borderDk};font-size:12px;transition:color .12s,background .12s,transform .12s}
.gf-drop:hover{background:${C.redLt};color:${C.red}}
.gf-drop:active{transform:scale(.85)}
`;

const muted: React.CSSProperties = { color: C.muted };
const tag = (bg: string, color: string): React.CSSProperties => ({ display: 'inline-block', padding: '0 5px', borderRadius: 4, background: bg, color, fontSize: 10.5, fontWeight: 700, lineHeight: '16px', whiteSpace: 'nowrap' });

function FitCell({ r }: { r: GovFeedRow }) {
  // ★ 했지만 아직 결과 없음 = 심층 분석 요청됨 (2026-10-03 교통정리)
  if (r.starred && r.fit == null && (r.aiStatus === 'none' || r.aiStatus === 'queued' || r.aiStatus === 'past'))
    return <span title="심층 분석 요청됨 — 차례로 분석합니다 (★ 를 다시 누르면 취소)" style={{ ...tag('#FEF3C7', '#B45309') }}>요청</span>;
  if (r.aiStatus === 'error') return <span style={{ fontSize: T.xs, fontWeight: 600, color: C.red }}>오류</span>;
  if (r.aiStatus === 'running') return <span style={{ fontSize: T.xs, color: C.accent, whiteSpace: 'nowrap' }}>분석중</span>;
  if (r.aiStatus === 'queued') return <span style={{ fontSize: T.xs, ...muted }}>대기</span>;
  if (r.aiStatus === 'past' && r.fit == null) return <span title="마감된 일회성 공고 — 자동 분석하지 않음" style={{ fontSize: T.xs, ...muted, whiteSpace: 'nowrap' }}>지난</span>;
  if (r.aiStatus === 'batched') return <span title="분석 대기(일괄) — 일괄 처리 중 — 보통 1시간 안에(최대 24시간) 결과" style={{ fontSize: T.xs, ...muted, whiteSpace: 'nowrap' }}>일괄</span>;
  if (r.fit == null) return <span style={{ fontSize: T.xs, ...muted }}>{r.screen === 'unfit' ? '비추천' : '대기'}</span>;
  const color = r.fit >= 70 ? C.green : r.fit >= 50 ? '#B45309' : C.muted;
  return <span className="gf-num" style={{ fontSize: T.md, fontWeight: 700, color }}>{r.fit}</span>;
}

/** 별 — 채운 것(관심) · 빈 것 */
export function StarIcon({ on, size = 15 }: { on: boolean; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
      <path d="M12 2.8l2.83 5.73 6.33.92-4.58 4.46 1.08 6.3L12 17.24l-5.66 2.97 1.08-6.3L2.84 9.45l6.33-.92L12 2.8z"
        fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={on ? 1.2 : 1.7} strokeLinejoin="round" />
    </svg>
  );
}

function SchedCell({ r, p }: { r: GovFeedRow; p: FeedProg }) {
  const proj = isProjected(r, p);
  const line = p.firstCome
    ? (p.applyStart ? `${shortDate(p.applyStart)} ~` : '')
    : p.deadline ? `${p.applyStart ? `${shortDate(p.applyStart)} ~ ` : '~ '}${shortDate(p.deadline, p.applyStart)}` : '';
  const always = !p.deadline && !p.firstCome;
  return (
    <div style={{ minWidth: 0 }}>
      <div className="gf-1 gf-num" style={line ? undefined : muted}>{line || (always ? '상시' : '미정')}</div>
      <div style={{ display: 'flex', gap: 3, marginTop: 2, height: 16 }}>
        {p.firstCome && <span style={tag(C.accentLt, C.accent)}>선착순</span>}
        {proj && <span style={tag(C.purpleLt, C.purple)}>내년 예상</span>}
        {p.recurYearly && !proj && <span style={tag(C.purpleLt, C.purple)}>↻ 매년</span>}
      </div>
    </div>
  );
}

interface TableProps {
  rows: GovFeedRow[];
  width: number;
  compact: boolean;
  selId: string | null;
  cursorId: string | null;
  checked: { has: (id: string) => boolean };
  rowErrors: Record<string, string>;
  savingIds: Set<string>;
  showReview: boolean;
  onCheck: (id: string, shift: boolean) => void;
  onCheckAll: () => void;
  onOpen: (id: string) => void;
  onEdit: (row: GovFeedRow, field: FeedEditField, anchor: DOMRect) => void;
  /** 관심 별표 켜기·끄기 — 없으면 별 칸을 숨긴다 */
  onStar?: (id: string, on: boolean) => void;
  /** 버리기(제외) — 분석 대기 탭에서 ☆ 옆에 ✕ (2026-10-04 지도사 「분석 or 버리기 클릭클릭하면서 내려가기」) · 없으면 칸을 숨긴다 */
  onDrop?: (id: string) => void;
  footer?: React.ReactNode;
  scrollRef?: React.Ref<HTMLDivElement>;
  /** 3단 검토에서 목록을 가는 띠(적합도만)로 */
  slim?: boolean;
  /** 띠 ↔ 좁은 목록 전환 */
  onToggleList?: () => void;
  /** 머리글 정렬 — 없으면 정렬 안 함 */
  sort?: GovFeedSort | null;
  onSort?: (k: GovFeedSortKey) => void;
}

export function GovFeedTable(props: TableProps) {
  const { rows, width, compact, selId, cursorId, checked, rowErrors, savingIds, showReview, onCheck, onCheckAll, onOpen, onEdit, onStar, onDrop, footer, scrollRef, slim, onToggleList, sort, onSort } = props;
  const cols = useMemo(() => visibleCols(width, compact, slim).filter(k => (k !== 'star' || !!onStar) && (k !== 'drop' || !!onDrop)), [width, compact, slim, onStar, onDrop]);
  const toggleBtn = onToggleList && (
    <button onClick={e => { e.stopPropagation(); onToggleList(); }} title={slim ? '목록 펼치기' : '목록 좁히기'} aria-label={slim ? '목록 펼치기' : '목록 좁히기'}
      style={{ width: 22, height: 22, borderRadius: 5, border: `1px solid ${C.border}`, background: C.white, color: C.sub, cursor: 'pointer', fontSize: 11, padding: 0, lineHeight: '20px' }}>{slim ? '»' : '«'}</button>
  );
  const colDefs = COLS.filter(c => cols.includes(c.k));
  const allOn = rows.length > 0 && rows.every(r => checked.has(r.id));
  const someOn = !allOn && rows.some(r => checked.has(r.id));
  const editable = !compact && !showReview;

  /** 정렬 가능한 머리글 — 누르면 정렬, 지금 정렬이면 ▲▼ */
  const sortLabel = (k: ColKey, label: string, align?: 'right' | 'center') => {
    const sk = SORT_OF[k];
    if (!sk || !onSort || slim) return label;
    const on = sort && sort.k === sk ? sort.dir : null;
    const df = sk === 'fit' || sk === 'amount' || sk === 'collected' ? 'desc' : 'asc';   // 처음 누르면 이 방향(onSort 와 같게)
    const next = !on ? (df === 'desc' ? '내림차순' : '오름차순') : on === df ? (df === 'asc' ? '내림차순' : '오름차순') : '원래 순서';
    return (
      <button type="button" className={`gf-sort${on ? ' on' : ''}`} onClick={e => { e.stopPropagation(); onSort(sk); }}
        title={`${SORT_TIP[sk]} — 눌러서 ${next}`} aria-sort={on === 'asc' ? 'ascending' : on === 'desc' ? 'descending' : 'none'}
        style={{ justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
        {label}<span className="gf-sort-ic" aria-hidden>{on === 'asc' ? '▲' : on === 'desc' ? '▼' : '↕'}</span>
      </button>
    );
  };

  const ed = (r: GovFeedRow, f: FeedEditField, node: React.ReactNode, title?: string) => (editable
    ? <span className="gf-ed" title={title || '눌러서 고치기'} onClick={e => { e.stopPropagation(); onEdit(r, f, (e.currentTarget as HTMLElement).getBoundingClientRect()); }}>{node}</span>
    : <span title={title}>{node}</span>);

  return (
    <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <style>{STYLE}</style>
      <table className="gf-tb">
        <colgroup>{colDefs.map(c => <col key={c.k} style={c.w ? { width: c.w } : undefined} />)}</colgroup>
        <thead>
          <tr>
            {colDefs.map(c => (
              <th key={c.k} style={{ textAlign: c.align || 'left', paddingLeft: c.k === 'chk' ? 12 : undefined }}>
                {slim && c.k === 'fit' ? toggleBtn
                  : c.k === 'name' && toggleBtn ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>{sortLabel(c.k, c.label, c.align)}{toggleBtn}</span>
                  : c.k === 'drop' ? <span title="버리기 — 관심 없는 공고를 치워 다음에 다시 보지 않게 (X) · 「처리 완료 › 제외됨」에서 되돌리기" style={{ color: C.borderDk, fontSize: 11, cursor: 'help' }}>✕</span>
                  : c.k === 'star' ? <span title="관심 — ☆를 눌러 관심 공고로 모아 두고 「★ 관심」에서 먼저 검토 (S)" style={{ color: C.borderDk, fontSize: 12, cursor: 'help' }}>★</span>
                  : c.k === 'chk'
                  ? <input type="checkbox" aria-label="보이는 행 전체 선택" checked={allOn} disabled={!rows.length}
                    ref={el => { if (el) el.indeterminate = someOn; }} onChange={onCheckAll} style={{ accentColor: C.accent, cursor: 'pointer', margin: 0 }} />
                  : sortLabel(c.k, c.label, c.align)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const p = programOf(r);
            const nm = displayNameOf(p); // 지난 공고는 리포트 사업명 (2026-10-01)
            const err = rowErrors[r.id];
            const cls = ['gf-r', r.starred && 'gf-starred', checked.has(r.id) && 'gf-chk', cursorId === r.id && 'gf-cur', selId === r.id && 'gf-sel'].filter(Boolean).join(' ');
            const region = p.regionDetails.length ? `${p.regions.length === 1 ? `${p.regions[0]} ` : ''}${p.regionDetails.join('·')}` : p.regions.join('·');
            const amount = p.amountMaxManwon != null ? fmtManwon(p.amountMaxManwon) : p.amount;
            const target = targetOf(p);
            return (
              <tr key={r.id} data-row={r.id} className={cls} onClick={() => onOpen(r.id)}
                style={{ opacity: showReview && r.review === 'excluded' ? 0.7 : 1 }}>
                {cols.includes('chk') && (
                  <td className="gf-cb" style={{ paddingLeft: 12 }} onMouseDown={e => { if (e.shiftKey) e.preventDefault(); }} onClick={e => { e.stopPropagation(); onCheck(r.id, e.shiftKey); }}>
                    <input type="checkbox" aria-label="선택" checked={checked.has(r.id)} readOnly style={{ accentColor: C.accent, cursor: 'pointer', margin: 0, pointerEvents: 'none' }} />
                  </td>
                )}
                {cols.includes('star') && onStar && (
                  <td style={{ textAlign: 'center', padding: 0 }} onClick={e => e.stopPropagation()}>
                    <button type="button" className={`gf-star${r.starred ? ' on' : ''}`} aria-pressed={!!r.starred} aria-label={r.starred ? '관심 해제' : '관심 표시'}
                      title={r.fit == null && (r.aiStatus === 'none' || r.aiStatus === 'queued' || r.aiStatus === 'past')
                        ? (r.starred ? '심층 분석 요청 취소 (S)' : '심층 분석 요청 (S) — 이 공고만 AI 가 자세히 분석')
                        : (r.starred ? '관심 해제 (S)' : '관심 표시 (S)')} onClick={e => { e.stopPropagation(); e.currentTarget.blur(); onStar(r.id, !r.starred); }}>
                      <StarIcon on={!!r.starred} />
                    </button>
                  </td>
                )}
                {cols.includes('drop') && onDrop && (
                  <td style={{ textAlign: 'center', padding: 0 }} onClick={e => e.stopPropagation()}>
                    <button type="button" className="gf-drop" aria-label="버리기" title="버리기 (X) — 다시 안 보임 · 바로 되돌리기 가능"
                      onClick={e => { e.stopPropagation(); e.currentTarget.blur(); onDrop(r.id); }}>✕</button>
                  </td>
                )}
                {cols.includes('fit') && (
                  <td style={{ textAlign: 'center', position: 'relative' }} title={slim ? `${nm}${r.unsureCount ? ` · 확인 필요 ${r.unsureCount}` : ''}` : undefined}>
                    <FitCell r={r} />
                    {slim && (r.unsureCount ?? 0) > 0 && <span style={{ position: 'absolute', top: 10, right: 8, width: 6, height: 6, borderRadius: 3, background: '#D97706' }} />}
                    {slim && r.starred && <span aria-label="관심" style={{ position: 'absolute', top: 6, left: 6, color: '#F59E0B', lineHeight: 0 }}><StarIcon on size={10} /></span>}
                    {slim && err && <span style={{ position: 'absolute', bottom: 10, right: 8, width: 6, height: 6, borderRadius: 3, background: C.red }} />}
                  </td>
                )}
                {cols.includes('name') && (compact ? (
                  <td title={`${nm}\n원공고: ${r.title}`}>
                    <div className={err ? 'gf-1' : 'gf-2'} style={{ fontSize: T.md, fontWeight: 600 }}>
                      {nm || '(이름 없음)'}
                      {r.edited && <> <span style={tag(C.accentLt, C.accent)}>수정됨</span></>}
                      {(r.dupCount ?? 0) > 0 && <> <span title={`같은 공고가 다른 채널에서 ${r.dupCount}건 더 들어와 하나로 묶었습니다`} style={tag(C.bg, C.sub)}>+{r.dupCount} 채널</span></>}
                      {r.dupProgramId && r.review !== 'approved' && <> <span style={tag(C.bg, C.sub)}>등록됨</span></>}
                    </div>
                    {err && <div className="gf-1" style={{ fontSize: T.xs, color: C.red, fontWeight: 600, marginTop: 1 }}>등록 실패 — {err}</div>}
                  </td>
                ) : (
                  <td>
                    {ed(r, 'name', (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                        <span className="gf-1" style={{ fontSize: T.md, fontWeight: 600 }}>{nm || '(이름 없음)'}</span>
                        {r.edited && <span style={tag(C.accentLt, C.accent)}>수정됨</span>}
                        {(r.dupCount ?? 0) > 0 && <span title={`같은 공고가 다른 채널에서 ${r.dupCount}건 더 들어와 하나로 묶었습니다 — 패널에서 보기`} style={tag(C.bg, C.sub)}>+{r.dupCount} 채널</span>}
                        {savingIds.has(r.id) && <span style={{ fontSize: T.xs, ...muted, whiteSpace: 'nowrap' }}>저장 중…</span>}
                        {r.dupProgramId && r.review !== 'approved' && <span style={tag(C.bg, C.sub)}>등록됨</span>}
                        {showReview && r.review === 'approved' && <span style={tag(C.greenLt, C.green)}>승인</span>}
                        {showReview && r.review === 'excluded' && <span style={tag(C.bg, C.muted)}>제외</span>}
                      </span>
                    ), `${nm}\n원공고: ${r.title}`)}
                    <div className="gf-1" title={err || r.title} style={{ fontSize: T.xs, color: err ? C.red : C.muted, marginTop: 1, fontWeight: err ? 600 : 400 }}>
                      {err ? `등록 실패 — ${err}` : [r.agency || r.ministry, r.title].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                ))}
                {cols.includes('region') && <td>{ed(r, 'region', <span className="gf-1" style={{ display: 'block', ...(region ? {} : muted) }}>{region || '전국'}</span>, region || '전국')}</td>}
                {cols.includes('cat') && <td>{ed(r, 'cat', <span className="gf-1" style={{ display: 'block', ...(p.category.length ? {} : muted) }}>{p.category.length ? p.category.join(' · ') : '미분류'}</span>, p.category.join(', '))}</td>}
                {cols.includes('amount') && <td style={{ textAlign: 'right' }}>{ed(r, 'amount', <span className="gf-1 gf-num" style={{ display: 'block', fontWeight: amount ? 600 : 400, ...(amount ? {} : muted) }}>{amount || '—'}</span>, p.amount || undefined)}</td>}
                {cols.includes('target') && <td><div className="gf-1" title={target} style={{ color: target ? C.sub : C.muted }}>{target || '제한 없음'}</div></td>}
                {cols.includes('sched') && <td><SchedCell r={r} p={p} /></td>}
                {cols.includes('unsure') && (
                  <td style={{ textAlign: 'center' }}>
                    {r.unsureCount != null && r.unsureCount > 0
                      ? <span className="gf-num" title={`확인 필요 ${r.unsureCount}개 항목`} style={{ ...tag(AMBER.chipBg, AMBER.text), fontSize: T.xs, padding: '1px 7px' }}>{r.unsureCount}</span>
                      : <span style={muted}>—</span>}
                  </td>
                )}
                {cols.includes('src') && <td title={`${(r.sources || []).map(k => SOURCE_LABEL[k] || k).join(', ')}${r.collectedAt ? ` · ${r.collectedAt} 불러옴` : ''}`}>
                  <div className="gf-1" style={{ fontSize: T.xs, color: C.muted }}>{(r.sources && r.sources.length ? r.sources : ['bizinfo']).map(k => SOURCE_LABEL[k] || k).join('·')}</div>
                  {r.collectedAt && <div className="gf-num" style={{ fontSize: T.xs, color: C.muted, opacity: 0.8, marginTop: 1, whiteSpace: 'nowrap' }}>({String(r.collectedAt).slice(5, 10).replace('-', '.')})</div>}
                </td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footer}
    </div>
  );
}

// ── 칸 편집 팝오버 ───────────────────────────────────────────────
const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.sub, marginBottom: 6 };
const inp: React.CSSProperties = { width: '100%', height: 36, padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' };

export function FeedCellEditor({ row, field, anchor, onClose, onSave }: {
  row: GovFeedRow; field: FeedEditField; anchor: DOMRect;
  onClose: () => void; onSave: (patch: Partial<GovProgram>) => Promise<boolean>;
}) {
  const p = useMemo(() => programOf(row), [row]);
  const [name, setName] = useState(p.name);
  const [reportName, setReportName] = useState(p.reportName);
  const past = isPastProg(p); // 지난 공고 — 리포트 사업명만 (FP 이름은 리포트 사업명을 따라간다)
  const [regions, setRegions] = useState<string[]>(p.regions);
  const [details, setDetails] = useState<string[]>(p.regionDetails);
  const [cats, setCats] = useState<string[]>(p.category);
  const [amount, setAmount] = useState(p.amount);
  const [manwon, setManwon] = useState(p.amountMaxManwon != null ? String(p.amountMaxManwon) : '');
  const manwonTouched = useRef(false);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (saving) return;
    let patch: Partial<GovProgram>;
    if (field === 'name') {
      if (past) {
        const rn = reportName.trim();
        if (!rn) return;
        const linked = !p.name.trim() || p.name.trim() === p.reportName.trim();
        patch = linked ? { report_name: rn, name: rn } : { report_name: rn };
      } else {
        if (!name.trim()) return;
        patch = { name: name.trim(), report_name: reportName.trim() };
      }
    }
    else if (field === 'region') patch = { region: regions.join(','), region_detail: details.join(',') };
    else if (field === 'cat') patch = { category: cats.join(','), tags: cats };
    else patch = { amount: amount.trim(), amount_max_manwon: manwon.trim() !== '' ? Number(manwon) : null };
    setSaving(true);
    const ok = await onSave(patch);
    setSaving(false);
    if (ok) onClose();
  }
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing && !(e.target instanceof HTMLTextAreaElement)) { e.preventDefault(); void save(); }
  };
  const width = field === 'cat' ? 460 : field === 'region' ? 420 : 380;

  return (
    <FeedPopover anchor={anchor} width={width} onClose={onClose}>
      <div onKeyDown={onKey}>
        {field === 'name' && (
          <>
            {!past && (
              <>
                <div style={lbl}>사업명 (FP 전송용) <span style={{ fontWeight: 400, color: C.muted }}>· [지역] 무엇을 + 금액 + 지원</span></div>
                <IMEText autoFocus value={name} onChange={setName} style={inp} />
              </>
            )}
            <div style={{ ...lbl, marginTop: past ? 0 : 10 }}>리포트 사업명 <span style={{ fontWeight: 400, color: C.muted }}>· 무슨 지원인지 알 수 있게(연도·지역·금액·기관 없이)</span></div>
            <IMEText autoFocus={past} value={reportName} onChange={setReportName} placeholder="예: 초기창업패키지" maxLength={200} style={inp} />
            {past && <div style={{ fontSize: T.xs, color: C.muted, marginTop: 6 }}>지난 공고 — FP 전송용 이름은 지금 신청 가능한 공고에만 필요합니다</div>}
            <div style={{ fontSize: T.xs, color: C.muted, marginTop: 6 }} className="gf-1" title={row.title}>원공고: {row.title}</div>
          </>
        )}
        {field === 'region' && (
          <>
            <div style={lbl}>지역 <span style={{ fontWeight: 400, color: C.muted }}>(복수 선택)</span></div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Pill active={regions.length === 0} onClick={() => { setRegions([]); setDetails([]); }} activeBorder="#9CA3AF" activeBg="#F3F4F6" activeColor="#374151">무관</Pill>
              {REGIONS.map(r => {
                const on = regions.includes(r);
                return <Pill key={r} active={on} activeBorder="#FAC775" activeBg="#FAEEDA" activeColor="#633806" onClick={() => {
                  if (on) { const cs = REGION_CITIES[r] || []; setRegions(v => v.filter(x => x !== r)); setDetails(v => v.filter(c => !cs.includes(c))); }
                  else setRegions(v => [...v, r]);
                }}>{r}</Pill>;
              })}
            </div>
            {regions.filter(r => REGION_CITIES[r]).map(r => (
              <div key={r} style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(250,199,117,0.27)' }}>
                <div style={{ fontSize: T.xs, color: '#854F0B', marginBottom: 5 }}>▾ {r} 시/군/구 <span style={{ color: C.muted }}>(선택 안 하면 {r} 전체)</span></div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  {REGION_CITIES[r].map(city => {
                    const on = details.includes(city);
                    return <Pill key={city} active={on} activeBorder="#FAC775" activeBg="#FAEEDA" activeColor="#633806"
                      onClick={() => setDetails(v => (on ? v.filter(x => x !== city) : [...v, city]))}>{city}</Pill>;
                  })}
                </div>
              </div>
            ))}
          </>
        )}
        {field === 'cat' && (
          <>
            <div style={lbl}>분류 <span style={{ fontWeight: 400, color: C.muted }}>(세부가 애매하면 대분류만)</span></div>
            <GovCategoryPicker compact value={cats.filter(c => !GOV_SPECIAL.includes(c))}
              onChange={next => setCats([...next, ...cats.filter(c => GOV_SPECIAL.includes(c))])} />
          </>
        )}
        {field === 'amount' && (
          <>
            <div style={lbl}>지원금액 <span style={{ fontWeight: 400, color: C.muted }}>(표기)</span></div>
            <IMEText autoFocus value={amount} placeholder="최대 1억원" style={inp}
              onChange={v => { setAmount(v); if (!manwonTouched.current) { const m = parseManwon(v); setManwon(m != null ? String(m) : ''); } }} />
            <div style={{ ...lbl, marginTop: 10 }}>숫자 <span style={{ fontWeight: 400, color: C.muted }}>(만원 · 표기에서 자동 계산)</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" min={0} value={manwon} onChange={e => { manwonTouched.current = true; setManwon(e.target.value); }} style={{ ...inp, width: 140 }} />
              <span className="gf-num" style={{ fontSize: T.sm, color: C.sub }}>{manwon ? `= ${fmtManwon(Number(manwon))}` : ''}</span>
            </div>
          </>
        )}
        <PopoverFoot onCancel={onClose} onSave={save} saving={saving} hint="⏎ 저장 · Esc 닫기" />
      </div>
    </FeedPopover>
  );
}

